import React, { useState, useEffect, useCallback, useRef } from 'react';
import { OpportunityTile, SparklineData, LOEStatus } from '../../useProductAnalysis.logic';
import OpportunityHero from './components/OpportunityHero/OpportunityHero';
import './OverviewTab.styles.scss';

interface OverviewTabProps {
  opportunityData: {
    tiles: OpportunityTile[];
    loeStatus: LOEStatus;
  };
  sparklineData: SparklineData;
  onOpportunityDataUpdate?: (
    tiles: OpportunityTile[],
    loeStatus: LOEStatus,
    sparklineData: SparklineData
  ) => void;
}

const OverviewTab: React.FC<OverviewTabProps> = ({
  opportunityData,
  sparklineData,
  onOpportunityDataUpdate,
}) => {
  const [currentTiles, setCurrentTiles] = useState<OpportunityTile[]>(opportunityData.tiles);
  const [currentLoeStatus, setCurrentLoeStatus] = useState<LOEStatus>(opportunityData.loeStatus);
  const [currentSparklineData, setCurrentSparklineData] = useState<SparklineData>(sparklineData);
  const prevDataRef = useRef<{
    tiles: OpportunityTile[];
    loeStatus: LOEStatus;
    sparklineData: SparklineData;
  } | null>(null);

  // Update local state when props change (with guards to prevent unnecessary updates)
  useEffect(() => {
    const tilesChanged =
      JSON.stringify(opportunityData.tiles) !== JSON.stringify(prevDataRef.current?.tiles);
    const loeChanged =
      JSON.stringify(opportunityData.loeStatus) !== JSON.stringify(prevDataRef.current?.loeStatus);
    const sparklineChanged =
      JSON.stringify(sparklineData) !== JSON.stringify(prevDataRef.current?.sparklineData);

    if (tilesChanged || loeChanged || sparklineChanged) {
      setCurrentTiles(opportunityData.tiles);
      setCurrentLoeStatus(opportunityData.loeStatus);
      setCurrentSparklineData(sparklineData);
      prevDataRef.current = {
        tiles: opportunityData.tiles,
        loeStatus: opportunityData.loeStatus,
        sparklineData: sparklineData,
      };
    }
  }, [opportunityData.tiles, opportunityData.loeStatus, sparklineData]);

  const handleDataUpdate = useCallback(
    (tiles: OpportunityTile[], loeStatus: LOEStatus, sparklineData: SparklineData) => {
      setCurrentTiles(prevTiles => {
        const tilesChanged = JSON.stringify(tiles) !== JSON.stringify(prevTiles);
        if (!tilesChanged) return prevTiles;
        return tiles;
      });

      setCurrentLoeStatus(prevLoe => {
        const loeChanged = JSON.stringify(loeStatus) !== JSON.stringify(prevLoe);
        if (!loeChanged) return prevLoe;
        return loeStatus;
      });

      setCurrentSparklineData(prevSparkline => {
        const sparklineChanged = JSON.stringify(sparklineData) !== JSON.stringify(prevSparkline);
        if (!sparklineChanged) return prevSparkline;
        return sparklineData;
      });

      // Notify parent if callback provided
      if (onOpportunityDataUpdate) {
        onOpportunityDataUpdate(tiles, loeStatus, sparklineData);
      }
    },
    [onOpportunityDataUpdate]
  );

  return (
    <div className="overview-tab">
      <OpportunityHero
        tiles={currentTiles}
        loeStatus={currentLoeStatus}
        sparklineData={currentSparklineData}
        onDataUpdate={handleDataUpdate}
      />
      {/* <MarketInsights /> */}
    </div>
  );
};

export default OverviewTab;
